import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { toast } from 'react-toastify';
import AddAccountModal from './AddAccountModal';

const TABS = [
    { key: 'all', label: 'All', icon: 'mdi:view-grid-outline' },
    { key: 'assets', label: 'Assets', icon: 'mdi:wallet-outline' },
    { key: 'liabilities', label: 'Liabilities', icon: 'mdi:credit-card-outline' },
    { key: 'income', label: 'Income', icon: 'mdi:arrow-down-circle-outline' },
    { key: 'expense', label: 'Expense', icon: 'mdi:arrow-up-circle-outline' },
    { key: 'miscellaneous', label: 'Miscellaneous', icon: 'mdi:dots-horizontal-circle-outline' },
];

const LEVEL_COLORS = [
    { bg: 'tw-bg-indigo-500', text: 'tw-text-white' },
    { bg: 'tw-bg-blue-500', text: 'tw-text-white' },
    { bg: 'tw-bg-sky-400', text: 'tw-text-white' },
    { bg: 'tw-bg-teal-400', text: 'tw-text-white' },
    { bg: 'tw-bg-emerald-400', text: 'tw-text-white' },
];

const AccountRow = ({ node, level = 0, expandedIds, toggleExpand, onView }) => {
    const isGroup = node.type === 'group';
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const indent = level * 22;
    const color = LEVEL_COLORS[Math.min(level, LEVEL_COLORS.length - 1)];
    const childCount = hasChildren ? node.children.length : 0;

    return (
        <>
            <tr
                className={`tw-group tw-border-b tw-border-gray-100/70 tw-transition-all tw-duration-150 ${
                    isGroup
                        ? level === 0
                            ? 'tw-bg-gradient-to-r tw-from-slate-50 tw-to-white hover:tw-from-blue-50/40 hover:tw-to-white'
                            : 'tw-bg-white hover:tw-bg-blue-50/30'
                        : 'tw-bg-white hover:tw-bg-emerald-50/20'
                }`}
                onClick={() => hasChildren && toggleExpand(node.id)}
                style={{ cursor: hasChildren ? 'pointer' : 'default' }}
            >
                {/* Account Code */}
                <td className="tw-py-2.5 tw-pr-3" style={{ paddingLeft: 20 + indent }}>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {/* Expand / Collapse */}
                        {hasChildren ? (
                            <div className={`tw-w-5 tw-h-5 tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-colors ${isExpanded ? 'tw-bg-blue-100' : 'tw-bg-gray-100 group-hover:tw-bg-blue-100'}`}>
                                <Icon
                                    icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                    className={`tw-text-sm tw-transition-colors ${isExpanded ? 'tw-text-blue-600' : 'tw-text-gray-400 group-hover:tw-text-blue-500'}`}
                                />
                            </div>
                        ) : (
                            <span className="tw-w-5 tw-flex-shrink-0" />
                        )}

                        {/* Icon */}
                        {isGroup ? (
                            <div className={`tw-w-6 tw-h-6 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color.bg} tw-shadow-sm`}>
                                <Icon icon="mdi:folder-outline" className={`tw-text-xs ${color.text}`} />
                            </div>
                        ) : (
                            <div className="tw-w-6 tw-h-6 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-bg-gray-100 tw-border tw-border-gray-200">
                                <Icon icon="mdi:file-document-outline" className="tw-text-xs tw-text-gray-400" />
                            </div>
                        )}

                        {/* Code */}
                        <code className={`tw-text-[11px] tw-px-1.5 tw-py-0.5 tw-rounded ${
                            isGroup
                                ? 'tw-bg-slate-100 tw-text-slate-700 tw-font-bold'
                                : 'tw-bg-gray-50 tw-text-gray-500 tw-font-medium'
                        }`}>
                            {node.ac_code}
                        </code>
                    </div>
                </td>

                {/* Account Name */}
                <td className="tw-px-3 tw-py-2.5">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-text-xs ${isGroup ? 'tw-font-semibold tw-text-gray-800' : 'tw-text-gray-600'}`}>
                            {node.name}
                        </span>
                        {isGroup && childCount > 0 && (
                            <span className="tw-text-[9px] tw-text-gray-400 tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-rounded-full tw-font-medium">
                                {childCount}
                            </span>
                        )}
                    </div>
                </td>

                {/* Type */}
                <td className="tw-px-3 tw-py-2.5">
                    {isGroup ? (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-blue-600 tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-blue-100">
                            <Icon icon="mdi:folder-outline" className="tw-text-[10px]" />
                            Group
                        </span>
                    ) : (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-emerald-600 tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-emerald-100">
                            <Icon icon="mdi:file-document-outline" className="tw-text-[10px]" />
                            Account
                        </span>
                    )}
                </td>

                {/* Details */}
                <td className="tw-px-3 tw-py-2.5">
                    {node.tag ? (
                        <span className="tw-text-[10px] tw-text-amber-600 tw-bg-amber-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-border tw-border-amber-100">
                            {node.tag}
                        </span>
                    ) : (
                        <span className="tw-text-[10px] tw-text-gray-300">-</span>
                    )}
                </td>

                {/* Actions — View opens the details modal. The three-dot
                    "More" button was removed because there are no BE
                    endpoints for create/edit/delete on chart of accounts,
                    so nothing to hang off it. */}
                <td className="tw-px-3 tw-py-2.5 tw-text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onView?.(node); }}
                            className="tw-p-1 tw-rounded-md hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors"
                            title="View details"
                        >
                            <Icon icon="mdi:eye-outline" className="tw-text-sm" />
                        </button>
                    </div>
                </td>
            </tr>
            {isExpanded && hasChildren && node.children.map(child => (
                <AccountRow
                    key={child.id}
                    node={child}
                    level={level + 1}
                    expandedIds={expandedIds}
                    toggleExpand={toggleExpand}
                    onView={onView}
                />
            ))}
        </>
    );
};

const ChartOfAccounts = () => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const [urlParams, setUrlParams] = useSearchParams();
    const activeTab = urlParams.get('type') || 'assets';
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedIds, setExpandedIds] = useState(new Set());
    // The row currently opened in the details modal (null when closed).
    // Keeps the whole node object so the modal can render every field
    // without a separate fetch.
    const [viewNode, setViewNode] = useState(null);
    const [addOpen, setAddOpen] = useState(null); // null | { parentId }

    const setActiveTab = (tab) => {
        setUrlParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('type', tab);
            return next;
        });
    };

    const fetchAccounts = useCallback(async () => {
        if (!societyId) return;
        setLoading(true);
        try {
            const url = apiEndpoints.GET_CHART_OF_ACCOUNTS_API.replace(':societyId', societyId);
            const params = {};
            if (activeTab !== 'all') params.type = activeTab;
            const response = await axiosInstance.get(url, { params });
            setTreeData(response?.data || []);
        } catch (err) {
            console.error('Failed to fetch chart of accounts:', err);
        } finally {
            setLoading(false);
        }
    }, [societyId, activeTab]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
    usePageRefresh(fetchAccounts);

    useEffect(() => {
        const firstLevel = new Set();
        treeData.forEach(n => { if (n.children?.length) firstLevel.add(n.id); });
        setExpandedIds(firstLevel);
    }, [treeData]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const collectAllGroupIds = (nodes) => {
        const ids = new Set();
        const walk = (list) => list.forEach(n => {
            if (n.children?.length) { ids.add(n.id); walk(n.children); }
        });
        walk(nodes);
        return ids;
    };

    const expandAll = () => setExpandedIds(collectAllGroupIds(treeData));
    const collapseAll = () => setExpandedIds(new Set());

    const stats = useMemo(() => {
        let total = 0, groups = 0, accounts = 0;
        const walk = (nodes) => nodes.forEach(n => {
            total++;
            if (n.type === 'group') groups++; else accounts++;
            if (n.children?.length) walk(n.children);
        });
        walk(treeData);
        return { total, groups, accounts };
    }, [treeData]);

    const filterTree = (nodes, query) => {
        if (!query) return nodes;
        const q = query.toLowerCase();
        return nodes.reduce((acc, node) => {
            const matches = node.name?.toLowerCase().includes(q) || node.ac_code?.toLowerCase().includes(q);
            const filteredChildren = node.children ? filterTree(node.children, query) : [];
            if (matches || filteredChildren.length > 0) {
                acc.push({ ...node, children: filteredChildren.length > 0 ? filteredChildren : node.children });
            }
            return acc;
        }, []);
    };

    const displayTree = filterTree(treeData, searchQuery);

    // Only groups can hold children — the API rejects a ledger account as a parent.
    const groupOptions = useMemo(() => {
        const out = [];
        const walk = (nodes, depth = 0) => {
            nodes.forEach(n => {
                if (n.type === 'group') {
                    out.push({ value: n.id, label: `${' '.repeat(depth * 3)}${n.ac_code} — ${n.name}` });
                    if (n.children?.length) walk(n.children, depth + 1);
                }
            });
        };
        walk(treeData);
        return out;
    }, [treeData]);

    const handleCreateAccount = async (form) => {
        const url = apiEndpoints.CREATE_CHART_OF_ACCOUNT_API.replace(':societyId', societyId);
        const res = await axiosInstance.post(url, form);
        if (res?.success) {
            toast.success(res.message || 'Account created');
            setAddOpen(null);
            fetchAccounts();
            return true;
        }
        toast.error(res?.message || 'Could not create the account');
        return false;
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-blue-500/20">
                        <Icon icon="mdi:file-tree" className="tw-text-xl tw-text-white" />
                    </div>
                    <div>
                        <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Chart of Accounts</h1>
                        <p className="tw-text-[11px] tw-text-gray-400">Manage your society's financial account hierarchy</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search accounts..."
                            className="tw-pl-8 tw-pr-3 tw-py-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-text-xs tw-w-52 focus:tw-outline-none focus:tw-border-blue-400 focus:tw-ring-2 focus:tw-ring-blue-100 tw-shadow-sm tw-transition-all"
                        />
                    </div>
                    <button
                        onClick={() => setAddOpen({ parentId: '' })}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-xl hover:tw-bg-blue-700 tw-shadow-sm tw-transition-all"
                    >
                        <Icon icon="mdi:plus" className="tw-text-sm" />
                        Add Account
                    </button>
                    <button onClick={expandAll} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl hover:tw-bg-gray-50 tw-shadow-sm tw-transition-all">
                        <Icon icon="mdi:unfold-more-horizontal" className="tw-text-sm" />
                        Expand
                    </button>
                    <button onClick={collapseAll} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl hover:tw-bg-gray-50 tw-shadow-sm tw-transition-all">
                        <Icon icon="mdi:unfold-less-horizontal" className="tw-text-sm" />
                        Collapse
                    </button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-5">
                {[
                    { label: 'Total', value: stats.total, icon: 'mdi:file-tree', bg: 'tw-bg-blue-50', color: 'tw-text-blue-500' },
                    { label: 'Groups', value: stats.groups, icon: 'mdi:folder-outline', bg: 'tw-bg-amber-50', color: 'tw-text-amber-500' },
                    { label: 'Accounts', value: stats.accounts, icon: 'mdi:file-document-outline', bg: 'tw-bg-emerald-50', color: 'tw-text-emerald-500' },
                ].map(card => (
                    <div key={card.label} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-px-4 tw-py-2.5 tw-flex tw-flex-row tw-items-center tw-gap-2.5 tw-shadow-sm" style={{ lineHeight: 1 }}>
                        <div className={`tw-w-8 tw-h-8 tw-rounded-lg ${card.bg} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={card.icon} width={16} height={16} className={card.color} />
                        </div>
                        <span className="tw-text-base tw-font-bold tw-text-gray-800">{card.value}</span>
                        <span className="tw-text-[10px] tw-text-gray-400 tw-font-medium">{card.label}</span>
                    </div>
                ))}
            </div>

            {/* Main Card */}
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
                {/* Tabs */}
                <div className="tw-border-b tw-border-gray-100 tw-px-5 tw-bg-gray-50/50">
                    <div className="tw-flex tw-gap-0 tw--mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-3 tw-text-xs tw-font-medium tw-border-b-2 tw-transition-all tw-duration-200 ${
                                    activeTab === tab.key
                                        ? 'tw-border-gray-800 tw-text-gray-800'
                                        : 'tw-border-transparent tw-text-gray-400 hover:tw-text-gray-600'
                                }`}
                            >
                                <Icon icon={tab.icon} className="tw-text-sm" />
                                {tab.label}
                                {activeTab === tab.key && (
                                    <span className="tw-text-[9px] tw-bg-gray-200 tw-text-gray-700 tw-px-1.5 tw-py-0.5 tw-rounded-full tw-font-bold tw-ml-0.5">
                                        {displayTree.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full">
                        <thead>
                            <tr className="tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider">Account Code</th>
                                <th className="tw-text-left tw-px-3 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider">Account Name</th>
                                <th className="tw-text-left tw-px-3 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-28">Type</th>
                                <th className="tw-text-left tw-px-3 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider">Tag</th>
                                <th className="tw-text-right tw-px-3 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-20">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="tw-text-center tw-py-20">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-animate-pulse">
                                                <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600"></div>
                                            </div>
                                            <span className="tw-text-gray-400 tw-text-xs">Loading chart of accounts...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayTree.length > 0 ? (
                                displayTree.map(node => (
                                    <AccountRow
                                        key={node.id}
                                        node={node}
                                        level={0}
                                        expandedIds={expandedIds}
                                        toggleExpand={toggleExpand}
                                        onView={setViewNode}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="tw-text-center tw-py-20">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:file-tree-outline" className="tw-text-3xl tw-text-gray-300" />
                                            </div>
                                            <p className="tw-text-gray-400 tw-text-xs tw-font-medium">No accounts found</p>
                                            <p className="tw-text-gray-300 tw-text-[10px]">Try adjusting your search or tab filter</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Details modal — opens when a row's View button is clicked.
                Pure display; no server fetch required because the tree
                response already carries every field we render. */}
            {addOpen && (
                <AddAccountModal
                    groups={groupOptions}
                    defaultParentId={addOpen.parentId}
                    onClose={() => setAddOpen(null)}
                    onSubmit={handleCreateAccount}
                />
            )}

            {viewNode && (
                <div
                    className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4"
                    onClick={() => setViewNode(null)}
                >
                    <div
                        className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${viewNode.type === 'group' ? 'tw-bg-blue-50' : 'tw-bg-emerald-50'}`}>
                                    <Icon
                                        icon={viewNode.type === 'group' ? 'mdi:folder-outline' : 'mdi:file-document-outline'}
                                        className={`tw-text-base ${viewNode.type === 'group' ? 'tw-text-blue-600' : 'tw-text-emerald-600'}`}
                                    />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Account Details</h3>
                                    <p className="tw-text-[10px] tw-text-gray-400">
                                        {viewNode.type === 'group' ? 'Group' : 'Account'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setViewNode(null)}
                                className="tw-w-7 tw-h-7 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-center"
                            >
                                <Icon icon="mdi:close" className="tw-text-lg" />
                            </button>
                        </div>
                        <div className="tw-px-5 tw-py-4 tw-space-y-3">
                            <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                <div className="tw-col-span-1">
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Code</p>
                                    <code className="tw-text-xs tw-font-bold tw-text-gray-800 tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-rounded">
                                        {viewNode.ac_code || '-'}
                                    </code>
                                </div>
                                <div className="tw-col-span-2">
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Name</p>
                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-break-words">{viewNode.name || '-'}</p>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Type</p>
                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-capitalize">{viewNode.type || '-'}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Status</p>
                                    <span className={`tw-inline-flex tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded ${
                                        viewNode.status === 'A' ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'
                                    }`}>
                                        {viewNode.status === 'A' ? 'Active' : (viewNode.status || '-')}
                                    </span>
                                </div>
                                {viewNode.type === 'group' && (
                                    <div>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Children</p>
                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{viewNode.children?.length || 0}</p>
                                    </div>
                                )}
                            </div>
                            {viewNode.tag && (
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Tag</p>
                                    <span className="tw-text-[11px] tw-text-amber-700 tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-rounded tw-border tw-border-amber-100">
                                        {viewNode.tag}
                                    </span>
                                </div>
                            )}
                            {viewNode.tag_for_reserve_funds && (
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Reserve Fund Tag</p>
                                    <p className="tw-text-xs tw-text-gray-700">{viewNode.tag_for_reserve_funds}</p>
                                </div>
                            )}
                            {viewNode.parent_account_info_id != null && (
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">Parent ID</p>
                                    <p className="tw-text-xs tw-text-gray-700 tw-font-mono">{viewNode.parent_account_info_id}</p>
                                </div>
                            )}
                        </div>
                        <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-bg-gray-50">
                            <button
                                onClick={() => setViewNode(null)}
                                className="tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-bg-white tw-rounded-lg hover:tw-bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChartOfAccounts;
